import React, { useEffect } from "react";
import { useApp } from "@/state/AppContext";
import { isWhiteLabelBusiness } from "@/lib/whiteLabel";
import {
  primaryActionFor,
  quickActionsDescriptor,
  quickActionsFor,
  sectionElementId,
  sectionsFor,
} from "@/storefront/registry";
import { StoreHero } from "@/components/storefront/StoreHero";
import { QuickActions } from "@/components/storefront/QuickActions";
import { BodyGrid } from "@/components/storefront/BodyGrid";
import { RailAd } from "@/components/storefront/RailAd";
import { OffersCard } from "@/components/storefront/OffersCard";
import { ContactRail } from "@/components/storefront/ContactRail";
import { Upsell } from "@/components/storefront/Upsell";
import { MobileActionBar } from "@/components/storefront/MobileActionBar";
import { AboutSection } from "@/components/storefront/AboutSection";
import { ReviewsSection } from "@/components/storefront/ReviewsSection";
import { CartSummaryCard } from "@/components/storefront/CartSummaryCard";
import { CollapsibleEnquiry } from "@/components/storefront/CollapsibleEnquiry";

/** The config-driven shell that replaces `BusinessMiniSite`'s cover-plus-tabs page: hero -> quick
 * actions -> a scrolling stacked body (capability sections, in order, then About, then Reviews)
 * with an offers/ad/contact aside — instead of one tab panel at a time. That's the one intended
 * visible change in this PR; every capability, flow, aside item and the mobile bar are otherwise
 * unchanged from `BusinessMiniSite`, just relocated into their own components (see registry.js
 * and each component's own docstring for what moved from where).
 *
 * `business` is `StorefrontOut` (PR2): `storefront_config` and `plans` arrive on it. `plans`
 * renders through `sectionsFor`'s `plans` pseudo-block (PR4, registry.js) like any other
 * section — it just has no `cap`, so it shows on data (`business.plans.length > 0`) rather than
 * a capability. White-label branding (PR5) publishes to `AppContext.storefrontBranding` so the
 * app-level Header/PortalShell can render it — see the effect below for why it's cleared on
 * unmount/business change rather than left to linger. */
export function Storefront({ business, offers, ads }) {
  const { t, lang, setStorefrontBranding } = useApp();
  const config = business.storefront_config || {};
  const sections = sectionsFor(business, config);
  const primaryAction = primaryActionFor(business, config, t);
  const quickActions = quickActionsFor(business, config, t);
  const quickDescriptor = quickActionsDescriptor(business, t, lang);
  // About/Reviews/Offers are not capability-gated (ARCHITECTURE.md §2b lists `about`/`gallery`
  // as togglable PSEUDO sections alongside `plans`, but only `plans` was ever wired into
  // `sectionsFor` — PR3/PR4 left these three unconditional). PR6's Website Settings panel needs
  // its "About"/"Offers"/"Reviews" toggles to do something real, so this is the minimal fix:
  // read the same `sections[key] !== false` convention `sectionsFor` already uses, here, for the
  // three sections it never covered. `gallery` has no toggle in the panel — no Gallery section
  // exists anywhere on the storefront to hide, so a toggle for it would fabricate a no-op.
  const showAbout = config.sections?.about !== false;
  const showReviews = config.sections?.reviews !== false;
  const showOffers = config.sections?.offers !== false;
  const whiteLabel = isWhiteLabelBusiness(business);

  // Publishes this business's branding for the in-app header to pick up, and clears it again on
  // unmount or whenever the business changes — the highest-risk bug in this PR is a stale brand
  // bleeding onto the next city page or the next business, so the cleanup runs on BOTH triggers,
  // not just unmount.
  useEffect(() => {
    if (whiteLabel) {
      setStorefrontBranding({
        id: business.id,
        logo_url: business.logo_url,
        brand_subtitle: business.brand_subtitle,
        brand_subtitle_hi: business.brand_subtitle_hi,
        hero_tagline: business.hero_tagline,
        hero_tagline_hi: business.hero_tagline_hi,
        name: business.name,
        phone: business.phone,
      });
    }
    return () => setStorefrontBranding(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business.id, whiteLabel]);

  return (
    <div className="flex flex-col pb-28 md:pb-0" data-testid="business-mini-site">
      <StoreHero business={business} offers={offers} />
      <QuickActions actions={quickActions} descriptor={quickDescriptor} businessId={business.id} />
      <BodyGrid
        main={
          <>
            {/* Storefront S5 (gap review item 1): the handoff's main column always runs About
                FIRST, then the offer/special card, then the vertical's own blocks (in
                `DEFAULT_ORDER`'s registry.js order, which already puts a capability's capture
                step — reserve/trial/appointment — after its browsing step), then Reviews. Built
                used to push About to the bottom (`design_handoff_business_websites/*.dc.html`
                BODY vs the old markup here). */}
            {showAbout && <AboutSection business={business} />}
            {showOffers && offers?.length > 0 && <OffersCard offers={offers} />}
            {sections.map((s) => (
              <div key={s.key} id={sectionElementId(s.key)} data-testid={`storefront-section-${s.key}`}>
                {/* Storefront S5 (gap review item 7): the generic enquiry form collapses behind a
                    "Have a question?" button whenever another specialised capture (ordering,
                    booking, attendance, plans, coverage, service visit…) already sits on this
                    page — it stays open when enquiry is the business's ONLY capture. */}
                {s.key === "enquiry" ? (
                  <CollapsibleEnquiry business={business} collapsed={sections.length > 1} />
                ) : (
                  <s.Component business={business} />
                )}
              </div>
            ))}
            {showReviews && <ReviewsSection business={business} />}
          </>
        }
        aside={
          <>
            {/* C2 — a persistent cart summary in the aside for the kirana/grocery grid layout
                (07_storefront_design_gap.md §2). Scoped to `business_type === "grocery"`: every
                other vertical keeps its existing "go to cart" button inside MenuSection, unchanged. */}
            {business.business_type === "grocery" && <CartSummaryCard business={business} />}
            <RailAd business={business} ad={ads?.rail} rate={ads?.rates?.rail} />
            <ContactRail business={business} />
            <Upsell business={business} />
          </>
        }
      />
      <MobileActionBar business={business} primaryAction={primaryAction} />
    </div>
  );
}
